import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { GridModule } from '@progress/kendo-angular-grid';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { FileVaultApiService } from '../../services';
import { FileHistory, FileVaultItem, FileAction } from '../../models';

export interface VersionHistoryConfig {
  showDownloadButton?: boolean;
  showRestoreButton?: boolean;
  showCompareButton?: boolean;
  maxItems?: number;
}

@Component({
  selector: 'fv-version-history',
  standalone: true,
  imports: [CommonModule, GridModule, ButtonsModule, IndicatorsModule, DialogModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="fv-version-history">
      @if (isLoading) {
        <div class="loading-container">
          <kendo-loader type="infinite-spinner" size="medium"></kendo-loader>
          <span>Loading history...</span>
        </div>
      } @else if (history.length === 0) {
        <div class="no-history">
          <span class="k-icon k-i-clock"></span>
          <p>No version history available</p>
        </div>
      } @else {
        <!-- Timeline View -->
        <div class="timeline">
          @for (item of history; track item.id; let i = $index) {
            <div class="timeline-item" [class.current]="i === 0">
              <div class="timeline-marker">
                <span class="version-badge">v{{ item.version }}</span>
              </div>
              
              <div class="timeline-content">
                <div class="timeline-header">
                  <span class="action-icon" [ngClass]="getActionIconClass(item.action)"></span>
                  <span class="action-label">{{ getActionLabel(item.action) }}</span>
                  <span class="timestamp">{{ item.timestamp | date:'medium' }}</span>
                </div>
                
                <div class="timeline-body">
                  <div class="user-info">
                    <span class="k-icon k-i-user"></span>
                    <span>{{ item.userName || 'Unknown' }}</span>
                  </div>
                  
                  @if (item.notes) {
                    <div class="notes">
                      <strong>Notes:</strong> {{ item.notes }}
                    </div>
                  }
                  
                  @if (item.actionDetails) {
                    <div class="details">{{ item.actionDetails }}</div>
                  }
                  
                  @if (item.fileSize) {
                    <div class="file-info">
                      <span class="k-icon k-i-file"></span>
                      <span>{{ item.fileName }}</span>
                      <span class="file-size">({{ formatFileSize(item.fileSize) }})</span>
                    </div>
                  }
                </div>
                
                <div class="timeline-actions">
                  @if (config.showDownloadButton !== false && item.archivedFilePath) {
                    <button kendoButton look="flat" (click)="onDownloadVersion(item)">
                      <span class="k-icon k-i-download"></span>
                      Download
                    </button>
                  }
                  @if (config.showRestoreButton !== false && i > 0) {
                    <button kendoButton look="flat" (click)="onRestoreVersion(item)">
                      <span class="k-icon k-i-undo"></span>
                      Restore
                    </button>
                  }
                  @if (config.showCompareButton !== false && i > 0 && i < history.length - 1) {
                    <button kendoButton look="flat" (click)="onCompareVersion(item, history[i + 1])">
                      <span class="k-icon k-i-track-changes-enable"></span>
                      Compare
                    </button>
                  }
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Grid View Toggle -->
        @if (showGridView) {
          <kendo-grid [data]="history" [height]="400">
            <kendo-grid-column field="version" title="Version" [width]="80">
              <ng-template kendoGridCellTemplate let-dataItem>
                <span class="version-badge small">v{{ dataItem.version }}</span>
              </ng-template>
            </kendo-grid-column>
            <kendo-grid-column field="action" title="Action" [width]="120">
              <ng-template kendoGridCellTemplate let-dataItem>
                {{ getActionLabel(dataItem.action) }}
              </ng-template>
            </kendo-grid-column>
            <kendo-grid-column field="userName" title="User" [width]="150"></kendo-grid-column>
            <kendo-grid-column field="timestamp" title="Date" [width]="180">
              <ng-template kendoGridCellTemplate let-dataItem>
                {{ dataItem.timestamp | date:'medium' }}
              </ng-template>
            </kendo-grid-column>
            <kendo-grid-column field="notes" title="Notes"></kendo-grid-column>
          </kendo-grid>
        }
      }

      <!-- Restore Confirmation Dialog -->
      @if (showRestoreDialog) {
        <kendo-dialog title="Restore Version" (close)="showRestoreDialog = false" [width]="400">
          <p>Are you sure you want to restore to version {{ selectedVersion?.version }}?</p>
          <p class="warning">
            <span class="k-icon k-i-warning"></span>
            This will create a new version with the contents of the selected version.
          </p>
          <kendo-dialog-actions>
            <button kendoButton (click)="showRestoreDialog = false">Cancel</button>
            <button kendoButton themeColor="primary" (click)="confirmRestore()">Restore</button>
          </kendo-dialog-actions>
        </kendo-dialog>
      }
    </div>
  `,
  styles: [`
    .fv-version-history {
      padding: 16px;
    }

    .loading-container, .no-history {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px;
      color: var(--fv-text-secondary);
      gap: 10px;
    }

    .no-history .k-icon {
      font-size: 48px;
      color: var(--fv-text-muted);
    }

    .timeline {
      position: relative;
      padding-left: 30px;
    }

    .timeline::before {
      content: '';
      position: absolute;
      left: 12px;
      top: 0;
      bottom: 0;
      width: 2px;
      background: var(--fv-border);
    }

    .timeline-item {
      position: relative;
      margin-bottom: 20px;
    }

    .timeline-item.current .timeline-marker .version-badge {
      background: var(--fv-text-accent);
    }

    .timeline-marker {
      position: absolute;
      left: -30px;
      top: 0;
    }

    .version-badge {
      display: inline-block;
      padding: 4px 8px;
      background: var(--fv-text-muted);
      color: white;
      border-radius: 12px;
      font-size: 0.8em;
      font-weight: 600;
    }

    .version-badge.small {
      padding: 2px 6px;
      font-size: 0.75em;
    }

    .timeline-content {
      background: var(--fv-surface);
      border: 1px solid var(--fv-border);
      border-radius: 8px;
      padding: 16px;
      margin-left: 20px;
    }

    .timeline-item.current .timeline-content {
      border-color: var(--fv-text-accent);
      box-shadow: 0 2px 8px var(--fv-shadow);
    }

    .timeline-header {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
    }

    .action-icon {
      font-size: 1.2em;
    }

    .action-icon.create { color: var(--fv-status-success-fg); }
    .action-icon.update { color: var(--fv-status-info-fg); }
    .action-icon.checkout { color: var(--fv-status-warning-fg); }
    .action-icon.checkin { color: var(--fv-status-success-fg); }
    .action-icon.delete { color: var(--fv-status-error-fg); }

    .action-label {
      font-weight: 600;
      color: var(--fv-text);
    }

    .timestamp {
      margin-left: auto;
      color: var(--fv-text-muted);
      font-size: 0.9em;
    }

    .timeline-body {
      color: var(--fv-text);
      font-size: 0.95em;
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: 6px;
      margin-bottom: 8px;
    }

    .notes {
      background: var(--fv-surface-hover);
      padding: 8px 12px;
      border-radius: 4px;
      margin-bottom: 8px;
    }

    .details {
      color: var(--fv-text-secondary);
      font-style: italic;
      margin-bottom: 8px;
    }

    .file-info {
      display: flex;
      align-items: center;
      gap: 6px;
      color: var(--fv-text-secondary);
    }

    .file-size {
      color: var(--fv-text-muted);
    }

    .timeline-actions {
      display: flex;
      gap: 8px;
      margin-top: 12px;
      padding-top: 12px;
      border-top: 1px solid var(--fv-border-subtle);
    }

    .warning {
      display: flex;
      align-items: center;
      gap: 8px;
      color: var(--fv-status-warning-fg);
      background: var(--fv-status-warning-bg);
      padding: 10px;
      border-radius: 4px;
    }
  `]
})
export class VersionHistoryComponent implements OnInit, OnChanges {
  @Input() fileId?: string;
  @Input() file?: FileVaultItem;
  @Input() config: VersionHistoryConfig = {
    showDownloadButton: true,
    showRestoreButton: true,
    showCompareButton: false
  };

  @Output() versionDownloaded = new EventEmitter<FileHistory>();
  @Output() versionRestored = new EventEmitter<FileHistory>();
  @Output() versionsCompared = new EventEmitter<{ from: FileHistory; to: FileHistory }>();

  history: FileHistory[] = [];
  isLoading = false;
  showGridView = false;
  showRestoreDialog = false;
  selectedVersion?: FileHistory;

  private destroy$ = new Subject<void>();

  constructor(
    private fileVaultApi: FileVaultApiService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadHistory();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['fileId'] || changes['file']) {
      this.loadHistory();
    }
  }

  loadHistory(): void {
    const id = this.fileId || this.file?.id;
    if (!id) return;

    this.isLoading = true;
    this.fileVaultApi.getFileHistory(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.history = data.sort((a, b) => 
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
          );
          
          if (this.config.maxItems) {
            this.history = this.history.slice(0, this.config.maxItems);
          }
          
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Failed to load history:', err);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  onDownloadVersion(item: FileHistory): void {
    this.versionDownloaded.emit(item);
    
    // Actually download the file
    if (item.fileId) {
      this.fileVaultApi.downloadFile(item.fileId, item.version)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = this.getDownloadFileName(item);
            a.click();
            window.URL.revokeObjectURL(url);
          },
          error: (err) => console.error('Download failed:', err)
        });
    }
  }

  private getDownloadFileName(item: FileHistory): string {
    const name = item.fileName?.trim();
    const pathName = this.getFileNameFromPath(item.filePath || item.archivedFilePath);
    if (name) {
      if (name.includes('.') || !pathName) {
        return name;
      }
      return pathName;
    }
    return pathName || 'file';
  }

  private getFileNameFromPath(path?: string): string | undefined {
    if (!path) return undefined;
    const normalized = path.replace(/\\/g, '/');
    const parts = normalized.split('/');
    return parts[parts.length - 1] || undefined;
  }

  onRestoreVersion(item: FileHistory): void {
    this.selectedVersion = item;
    this.showRestoreDialog = true;
  }

  confirmRestore(): void {
    if (!this.selectedVersion) return;
    
    const fileId = this.fileId || this.file?.id;
    if (!fileId) return;

    this.fileVaultApi.restoreVersion(fileId, this.selectedVersion.version)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.versionRestored.emit(this.selectedVersion);
          this.showRestoreDialog = false;
          this.loadHistory(); // Refresh
        },
        error: (err) => {
          console.error('Restore failed:', err);
          this.showRestoreDialog = false;
        }
      });
  }

  onCompareVersion(from: FileHistory, to: FileHistory): void {
    this.versionsCompared.emit({ from, to });
  }

  getActionLabel(action: FileAction): string {
    const labels: Record<number, string> = {
      [FileAction.None]: 'None',
      [FileAction.Create]: 'Created',
      [FileAction.Read]: 'Read',
      [FileAction.Update]: 'Updated',
      [FileAction.Delete]: 'Deleted',
      [FileAction.CheckOut]: 'Checked Out',
      [FileAction.CheckIn]: 'Checked In',
      [FileAction.UndoCheckOut]: 'Undo Checkout',
      [FileAction.Lock]: 'Locked',
      [FileAction.Unlock]: 'Unlocked',
      [FileAction.Move]: 'Moved',
      [FileAction.Copy]: 'Copied',
      [FileAction.Rename]: 'Renamed',
      [FileAction.Archive]: 'Archived',
      [FileAction.Restore]: 'Restored',
      [FileAction.Download]: 'Downloaded',
      [FileAction.Upload]: 'Uploaded'
    };
    return labels[action] || 'Unknown';
  }

  getActionIconClass(action: FileAction): string {
    const icons: Record<number, string> = {
      [FileAction.Create]: 'k-icon k-i-plus create',
      [FileAction.Update]: 'k-icon k-i-edit update',
      [FileAction.Delete]: 'k-icon k-i-trash delete',
      [FileAction.CheckOut]: 'k-icon k-i-lock checkout',
      [FileAction.CheckIn]: 'k-icon k-i-unlock checkin',
      [FileAction.Upload]: 'k-icon k-i-upload create',
      [FileAction.Download]: 'k-icon k-i-download',
      [FileAction.Move]: 'k-icon k-i-arrow-right',
      [FileAction.Copy]: 'k-icon k-i-copy',
      [FileAction.Restore]: 'k-icon k-i-undo'
    };
    return icons[action] || 'k-icon k-i-clock';
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  toggleView(): void {
    this.showGridView = !this.showGridView;
  }

  refresh(): void {
    this.loadHistory();
  }
}
